/**
 * UndoService - Manages undo/redo history for application state
 * 
 * ## Design
 * - Stores up to MAX_HISTORY (150) snapshots of application state
 * - Uses JSON serialization for deep cloning
 * - Emits events for UI updates (button enable/disable)
 * 
 * ## Usage
 * - Call `saveState(state)` after each user action that should be undoable
 * - Call `undo()` / `redo()` to navigate history
 * - Listen to 'undo:state-change' event for history availability updates
 * 
 * ## Performance
 * - State snapshots are JSON strings (efficient storage, fast comparison)
 * - O(1) undo/redo operations
 * - Automatic pruning when history exceeds MAX_HISTORY
 */

export const MAX_HISTORY = 150;

export class UndoService {
  /**
   * @param {Object} eventBus - Event bus for emitting state change events
   */
  constructor(eventBus) {
    this.eventBus = eventBus;
    
    /** @type {string[]} Stack of serialized state snapshots */
    this._undoStack = [];
    
    /** @type {string[]} Stack of states for redo */
    this._redoStack = [];
    
    /** @type {string|null} Last saved state to detect duplicates */
    this._lastState = null;
  }
  
  /**
   * Save current state to undo history.
   * Call this after each undoable user action.
   * 
   * @param {Object} state - Application state to save (will be JSON serialized)
   * @param {Object} [options]
   * @param {number} [options.discardOldest=0] - Additional oldest snapshots
   *   to discard after the ordinary MAX_HISTORY rollover.
   * @returns {{saved: boolean, automaticDiscardCount: number, additionalDiscardCount: number}}
   */
  saveState(state, { discardOldest = 0 } = {}) {
    if (!Number.isInteger(discardOldest) || discardOldest < 0) {
      throw new Error('Additional history discard count must be a non-negative integer');
    }

    const preview = this.previewSaveState(state);
    if (!preview.saved) {
      if (discardOldest !== 0) {
        throw new Error('Cannot discard undo history without saving a new state');
      }
      return { saved: false, automaticDiscardCount: 0, additionalDiscardCount: 0 };
    }

    const maxAdditionalDiscard = Math.max(0, preview.undoStack.length - 1);
    if (discardOldest > maxAdditionalDiscard) {
      throw new Error('Cannot discard the current undo state');
    }

    this._undoStack = preview.undoStack.slice(discardOldest);
    this._redoStack = preview.redoStack;
    this._lastState = preview.lastState;
    this._emitStateChange();
    return {
      saved: true,
      automaticDiscardCount: preview.automaticDiscardCount,
      additionalDiscardCount: discardOldest,
    };
  }

  /**
   * Preview the exact stacks a successful save would retain without mutating
   * history. Asset admission uses this to account for normal redo invalidation
   * and MAX_HISTORY rollover before considering extra history loss.
   * @param {Object} state
   * @returns {{saved: boolean, undoStack: string[], redoStack: string[], lastState: string|null, automaticDiscardCount: number}}
   */
  previewSaveState(state) {
    const serialized = JSON.stringify(state);
    if (serialized === this._lastState) {
      return {
        saved: false,
        undoStack: [...this._undoStack],
        redoStack: [...this._redoStack],
        lastState: this._lastState,
        automaticDiscardCount: 0,
      };
    }

    const undoStack = [...this._undoStack, serialized];
    const automaticDiscardCount = Math.max(0, undoStack.length - MAX_HISTORY);
    return {
      saved: true,
      undoStack: undoStack.slice(automaticDiscardCount),
      // A successful new state always starts a new branch.
      redoStack: [],
      lastState: serialized,
      automaticDiscardCount,
    };
  }
  
  /**
   * Undo the last action.
   * @returns {Object|null} Previous state to restore, or null if nothing to undo
   */
  undo() {
    if (this._undoStack.length <= 1) {
      // Need at least 2 states: current + previous
      return null;
    }
    
    // Pop current state and move to redo stack
    const currentState = this._undoStack.pop();
    this._redoStack.push(currentState);
    
    // Get previous state (don't pop, it's now current)
    const previousState = this._undoStack[this._undoStack.length - 1];
    this._lastState = previousState;
    
    this._emitStateChange();
    return JSON.parse(previousState);
  }
  
  /**
   * Redo the last undone action.
   * @returns {Object|null} Next state to restore, or null if nothing to redo
   */
  redo() {
    if (this._redoStack.length === 0) {
      return null;
    }
    
    // Pop from redo stack and push to undo stack
    const nextState = this._redoStack.pop();
    this._undoStack.push(nextState);
    this._lastState = nextState;
    
    this._emitStateChange();
    return JSON.parse(nextState);
  }
  
  /**
   * Check if undo is available
   * @returns {boolean}
   */
  canUndo() {
    return this._undoStack.length > 1;
  }
  
  /**
   * Check if redo is available
   * @returns {boolean}
   */
  canRedo() {
    return this._redoStack.length > 0;
  }
  
  /**
   * Clear all history (e.g., when loading a new project)
   */
  clear() {
    this._undoStack = [];
    this._redoStack = [];
    this._lastState = null;
    this._emitStateChange();
  }

  /**
   * Replace history with one validated baseline snapshot. Loading a project
   * must not leave undo entries that refer to objects from the prior project.
   * @param {Object} state
   */
  reset(state) {
    const serialized = JSON.stringify(state);
    this._undoStack = [serialized];
    this._redoStack = [];
    this._lastState = serialized;
    this._emitStateChange();
  }

  /**
   * Capture history for transactional rollback around a project commit.
   * @returns {{undoStack: string[], redoStack: string[], lastState: string|null}}
   */
  createSnapshot() {
    return {
      undoStack: [...this._undoStack],
      redoStack: [...this._redoStack],
      lastState: this._lastState,
    };
  }

  /**
   * Return immutable serialized roots for reference accounting without
   * exposing the mutable history arrays themselves.
   * @returns {string[]}
   */
  getRetainedSerializedStates() {
    return [...this._undoStack, ...this._redoStack];
  }

  /**
   * Restore a snapshot created by createSnapshot().
   * @param {{undoStack: string[], redoStack: string[], lastState: string|null}} snapshot
   */
  restoreSnapshot(snapshot) {
    if (!snapshot || !Array.isArray(snapshot.undoStack) || !Array.isArray(snapshot.redoStack)) {
      throw new Error('Invalid undo history snapshot');
    }
    this._undoStack = [...snapshot.undoStack];
    this._redoStack = [...snapshot.redoStack];
    this._lastState = snapshot.lastState ?? null;
    this._emitStateChange();
  }
  
  /**
   * Emit event with current undo/redo availability
   * @private
   */
  _emitStateChange() {
    this.eventBus?.emit('undo:state-change', {
      canUndo: this.canUndo(),
      canRedo: this.canRedo(),
      undoCount: this._undoStack.length - 1,
      redoCount: this._redoStack.length
    });
  }
}
